import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Save, RefreshCw, Upload, Search, CheckCircle, AlertCircle, FileText, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  submersible: "Submersible",
  booster: "Booster",
  monoblock: "Monoblock",
  openwell: "Openwell",
  sewage: "Sewage",
  dewatering: "Dewatering",
};

export default function CatalogAdmin() {
  const [pumps, setPumps] = useState([]);
  const [edits, setEdits] = useState({}); // {model: newPrice}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [toast, setToast] = useState(null); // {type, text}
  const fileInputRef = useRef(null);

  const fetchPumps = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/catalog/pumps`);
      setPumps(r.data.pumps || []);
      setEdits({});
    } catch (e) {
      console.error(e);
      showToast("error", "Failed to load catalog");
    }
    setLoading(false);
  };

  useEffect(() => { fetchPumps(); }, []);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const categories = useMemo(() => {
    const s = new Set(pumps.map(p => p.category).filter(Boolean));
    return Array.from(s).sort();
  }, [pumps]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pumps.filter(p => {
      if (category !== "all" && p.category !== category) return false;
      if (!q) return true;
      const hay = `${p.model || ""} ${p.name || ""} ${p.brand || ""} ${p.use_case || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pumps, search, category]);

  const setNewPrice = (model, val) => {
    setEdits(prev => {
      const next = { ...prev };
      if (val === "" || val === null) {
        delete next[model];
      } else {
        next[model] = val;
      }
      return next;
    });
  };

  const dirtyCount = Object.keys(edits).length;

  const saveAll = async () => {
    const updates = Object.entries(edits)
      .map(([model, price]) => ({ model, price: Number(price) }))
      .filter(u => u.model && !Number.isNaN(u.price) && u.price >= 0);
    if (!updates.length) {
      showToast("error", "No valid changes to save");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/catalog/prices`, { updates });
      showToast("success", r.data.message || `Updated ${r.data.applied} prices`);
      await fetchPumps();
    } catch (e) {
      console.error(e);
      showToast("error", e?.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const onCsvPick = () => fileInputRef.current?.click();

  const onCsvChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv") && !f.name.toLowerCase().endsWith(".txt")) {
      showToast("error", "Please upload a .csv file");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await axios.post(`${API}/admin/catalog/prices/csv`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showToast("success", r.data.message || "CSV applied");
      await fetchPumps();
    } catch (e) {
      console.error(e);
      showToast("error", e?.response?.data?.detail || "Failed to upload CSV");
    }
    setUploading(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA] p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-[#1A1714]">Admin</h1>
          <button
            onClick={fetchPumps}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[#3D3630] bg-white rounded-lg border border-[#D5CCBC] hover:bg-[#EDEAD4] disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-5 border-b border-[#D5CCBC]">
          <Link
            to="/admin/leads"
            className="px-4 py-2.5 text-sm font-medium text-[#7A6F66] hover:text-[#B22222]"
          >
            Leads
          </Link>
          <div className="px-4 py-2.5 text-sm font-semibold text-[#B22222] border-b-2 border-[#B22222] -mb-[1px]">
            Catalog
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-xl border border-[#D5CCBC] p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6F66]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by model, brand, use case..."
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#D5CCBC] text-sm outline-none focus:border-[#B22222] focus:ring-2 focus:ring-[#B22222]/20 bg-white"
              />
            </div>
            <div className="relative">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="appearance-none pl-3 pr-9 py-2.5 rounded-lg border border-[#D5CCBC] text-sm outline-none focus:border-[#B22222] bg-white text-[#3D3630] min-w-[160px]"
              >
                <option value="all">All categories</option>
                {categories.map(c => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A6F66] pointer-events-none" />
            </div>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={onCsvChange}
                data-testid="csv-file-input"
              />
              <button
                onClick={onCsvPick}
                disabled={uploading}
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg border border-[#D5CCBC] bg-white hover:bg-[#EDEAD4] disabled:opacity-50"
                data-testid="csv-upload-btn"
              >
                {uploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                <span className="hidden sm:inline">Upload CSV</span>
              </button>
              <button
                onClick={saveAll}
                disabled={saving || dirtyCount === 0}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-lg text-white disabled:opacity-40"
                style={{ backgroundColor: "#B22222" }}
                data-testid="save-prices-btn"
              >
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                Save {dirtyCount > 0 ? `(${dirtyCount})` : ""}
              </button>
            </div>
          </div>

          <div className="mt-3 flex items-start gap-2 text-xs text-[#7A6F66]">
            <FileText size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              CSV format: header row with <code className="bg-[#F5F1EA] px-1 rounded">model_name</code> and <code className="bg-[#F5F1EA] px-1 rounded">new_price</code>. Extra columns are ignored.
            </span>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div
            className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${
              toast.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"
            }`}
            data-testid="admin-toast"
          >
            {toast.type === "success" ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {toast.text}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-[#D5CCBC] overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-[#7A6F66] text-sm">Loading catalog...</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-[#7A6F66] text-sm">No pumps match your filter</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F1EA] text-[#7A6F66] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Model</th>
                      <th className="px-4 py-3 text-left">Category</th>
                      <th className="px-4 py-3 text-left">HP</th>
                      <th className="px-4 py-3 text-right">Current Price</th>
                      <th className="px-4 py-3 text-right">New Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EDEAD4]">
                    {filtered.map(p => {
                      const dirty = edits[p.model] !== undefined;
                      return (
                        <tr key={p.model} className={dirty ? "bg-[#FEF7F7]" : ""} data-testid={`pump-row-${p.model}`}>
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-[#1A1714]">{p.model}</div>
                            <div className="text-xs text-[#7A6F66]">{p.brand} {p.use_case ? `· ${p.use_case.replace(/_/g, " ")}` : ""}</div>
                          </td>
                          <td className="px-4 py-2.5 text-[#3D3630] capitalize">{CATEGORY_LABELS[p.category] || p.category || "-"}</td>
                          <td className="px-4 py-2.5 text-[#3D3630]">{p.hp ?? "-"}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-[#3D3630]">
                            {p.price != null ? `₹${Number(p.price).toLocaleString("en-IN")}` : "-"}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <input
                              type="number"
                              min="0"
                              value={edits[p.model] ?? ""}
                              onChange={(e) => setNewPrice(p.model, e.target.value)}
                              placeholder={p.price != null ? String(p.price) : "0"}
                              className={`w-28 px-2 py-1.5 rounded-md border text-right text-sm outline-none bg-white ${
                                dirty ? "border-[#B22222] ring-2 ring-[#B22222]/20" : "border-[#D5CCBC]"
                              }`}
                              data-testid={`price-input-${p.model}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-[#EDEAD4]">
                {filtered.map(p => {
                  const dirty = edits[p.model] !== undefined;
                  return (
                    <div key={p.model} className={`p-4 ${dirty ? "bg-[#FEF7F7]" : ""}`} data-testid={`pump-card-${p.model}`}>
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#1A1714] truncate">{p.model}</div>
                          <div className="text-xs text-[#7A6F66] mt-0.5">
                            {CATEGORY_LABELS[p.category] || p.category || "-"} · {p.hp ?? "-"}HP · {p.brand}
                          </div>
                          <div className="text-xs text-[#7A6F66] mt-0.5">
                            Current: <span className="font-mono">₹{Number(p.price || 0).toLocaleString("en-IN")}</span>
                          </div>
                        </div>
                        <input
                          type="number"
                          min="0"
                          value={edits[p.model] ?? ""}
                          onChange={(e) => setNewPrice(p.model, e.target.value)}
                          placeholder={p.price != null ? String(p.price) : "0"}
                          className={`w-28 px-2 py-2 rounded-md border text-right text-sm outline-none bg-white flex-shrink-0 ${
                            dirty ? "border-[#B22222] ring-2 ring-[#B22222]/20" : "border-[#D5CCBC]"
                          }`}
                          data-testid={`price-input-mobile-${p.model}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer info */}
        <div className="text-xs text-[#7A6F66] mt-4 text-center">
          Showing {filtered.length} of {pumps.length} pumps
        </div>
      </div>
    </div>
  );
}
